import { useMemo, useCallback, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import {
  Autocomplete,
  TextField,
  FormHelperText,
  Tooltip,
  InputAdornment,
  ListItem,
  createTheme,
} from "@mui/material";
import { FnComponents, I18n, ParseHtml } from "utilities/utilities";
import { I18NextNs, TypeComponent } from "enum/enum";
import { ISelectItemProps } from "./SelectProps";


import * as locales from '@mui/material/locale';
import { ThemeProvider, useTheme } from "@emotion/react";
import { PopperCustom } from ".";
type SupportedLocales = keyof typeof locales;

export const SelectItem = (props: ISelectItemProps) => {
  const {
    id,
    name,
    label,
    fullWidth = true,
    defaultValue = null,
    IsShowMessageError = true,
    IsClearable = false,
    disabled = false,
    required = false,
    size= "small",
    IsNotShowLabel = false,
    IsPopperCustom = false,
    zIndex = 2000
  } = props;

  const { control,register } = useFormContext();

  const [locale, setLocale] = useState<SupportedLocales>('thTH');
  const theme = useTheme();
  const themeWithLocale = useMemo(
    () => createTheme(theme, locales[locale]),
    [locale, theme],
  );

  const rules = useMemo(() => {
    return {
      required: {
        value: required,
        message: `${I18n.SetText("required", I18NextNs.validation)} ${label}`,
      },
    };
  }, [required, label]);
  
  const getOpObjValue = (val) => {
    let res = null;
    if (val) res = props.options.find((op) => op.value === val);
    return res;
  };

  const onRenderOption = useCallback((props, option: any) => {
    return (
      <ListItem
        {...props}
        key={option.value}
        style={
          option.color ? { backgroundColor: option.color } : {}
        }
      >
        {option.label}
      </ListItem>
    );
  }, []);

  const onFilterOptions = useCallback((options, params) => {
    const filtered = params.inputValue
      ? options.filter((f: any) =>
        f.label
          .toLowerCase()
          .includes(params.inputValue.toLowerCase())
      )
      : options;
    return props.limits != null
      ? filtered.slice(0, props.limits)
      : filtered;
  }, [props.limits]);

  const onHandleOnchange = useCallback((event, value, onChangeController) => {
    onChangeController(value != null ? value["value"] : null);
    if (props.onChange) {
      props.onChange(value, event);
    }
  }, [props]);

  const defaultCheckOption = useMemo(() => {
    let result = props.options.findIndex((op) => op.value === defaultValue) === -1 ? null : defaultValue;    
    return result;
  }, [defaultValue, props.options])


  
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      shouldUnregister={true}
      defaultValue={defaultCheckOption}
      render={({
        field: { onChange, onBlur, value, ref },
        fieldState: { invalid, error },
      }) => {
        return (
          <ThemeProvider theme={themeWithLocale}>
            <Autocomplete
              {...register(name)}
              ref={ref}
              id={FnComponents.GetId(TypeComponent.select, id, name)}
              disabled={disabled}
              PopperComponent={IsPopperCustom ? PopperCustom : undefined}
              disablePortal={false}
              fullWidth={fullWidth}
              size={size}
              options={props.options || []}
              value={getOpObjValue(value)}
              noOptionsText={
                props.notOptionsText || I18n.SetText("noInformationFound")
              }
              disableClearable={IsClearable}
              renderOption={onRenderOption}
              filterOptions={onFilterOptions}
              getOptionLabel={(itemOption: any) => {
                return `${itemOption?.label}`;
              }}
              slotProps={{
                popper: {
                  sx: {
                    zIndex: zIndex
                  }
                }
              }}
              renderInput={(params) => {
                let TooltipTitle = null;
                if (disabled) {
                  TooltipTitle = params.inputProps.value;
                }
                return (
                  <Tooltip
                    title={ParseHtml(TooltipTitle)}
                    disableHoverListener={!TooltipTitle}
                    disableFocusListener
                  >
                    <TextField
                      {...params}
                      name={name}
                      id={FnComponents.GetId(TypeComponent.input, id, name)}
                      error={invalid}
                      required={props.required}
                      disabled={disabled}
                      label={IsNotShowLabel === true ? "" : props.label}
                      placeholder={props.placeholder}
                      size={size}
                      fullWidth={fullWidth}
                      style={props.style}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: props.startAdornment ? (
                          <>
                            <InputAdornment position="start">
                              {props.startAdornment}
                            </InputAdornment>
                            params.InputProps.startAdornment
                          </>
                        ) : (
                          params.InputProps.startAdornment
                        ),
                      }}
                    />
                  </Tooltip>
                );
              }}
              onChange={(event, value) => { 
                onHandleOnchange(event, value, onChange) 
              }}
              onBlur={(event) => {
                onBlur();
                if (props.onBlur) {
                  props.onBlur(event);
                }
              }}
              onKeyDown={(event) => {
                if (props.onKeyDown) {
                  props.onKeyDown(event);
                }
              }}
              onKeyUp={(event) => {
                if (props.onKeyUp) {
                  props.onKeyUp(event);
                }
              }}
            />
            {IsShowMessageError && error ? (
              <FormHelperText>
                {error.message}
              </FormHelperText>
            ) : null}
          </ThemeProvider>
        );
      }}
    />
  );
};